import React, { useEffect, useState, useCallback } from 'react';
import { GoogleMap, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Typography, CircularProgress, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header from '../components/Header';
import { tokens } from '../theme';
import { API_BASE_URL } from '../config';

const mapContainerStyle = {
  width: '100%',
  height: '70vh',
  borderRadius: '8px'
};

const center = {
  lat: 36.752887,
  lng: 3.042048
};

const RideMap = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [rideInfo, setRideInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const { id } = useParams();
  const navigate = useNavigate();

  const fetchRideDetails = useCallback(async () => {
    const authToken = sessionStorage.getItem("authToken");
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/ride`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authToken, ride_id: id }),
      });
      const data = await response.json();
      if (data.success && data.ride) {
        setRideInfo(data.ride);
        const { start_lat, start_lng, end_lat, end_lng } = data.ride;
        if (start_lat && start_lng) {
          setStart({ lat: parseFloat(start_lat), lng: parseFloat(start_lng) });
        }
        if (end_lat && end_lng) {
          setEnd({ lat: parseFloat(end_lat), lng: parseFloat(end_lng) });
        }
      }
    } catch (error) {
      console.error('Error fetching ride details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRideDetails();
  }, [fetchRideDetails]);

  useEffect(() => {
    if (start && end && typeof window !== 'undefined' && window.google?.maps?.DirectionsService) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: start,
          destination: end,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirectionsResponse(result);
          } else {
            console.error('Error fetching directions:', status);
          }
        }
      );
    }
  }, [start, end]);

  const hasGoogle = typeof window !== 'undefined' && window.google && window.google.maps;

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
        <Header
          title={`TRACÉ DE LA COURSE #${id}`}
          subtitle={rideInfo ? `${rideInfo.price} DZD • ${rideInfo.distance || 0} km • ${rideInfo.duration || 0} min` : "Visualisation de l'itinéraire"}
        />
        <Button
          onClick={() => navigate(-1)}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ color: colors.grey[100], borderColor: colors.grey[500] }}
        >
          Retour aux courses
        </Button>
      </Box>

      <Box height="70vh" backgroundColor="#1F2A40" borderRadius="8px" display="flex" justifyContent="center" alignItems="center">
        {!hasGoogle || loading ? (
          <Box textAlign="center" color="#fff">
            <CircularProgress color="secondary" />
            <Typography mt={2}>Chargement de la carte et du tracé...</Typography>
          </Box>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={start || center}
            zoom={12}
            options={{ disableDefaultUI: false }}
          >
            {start && (
              <Marker
                position={start}
                title="Point de prise en charge"
                icon={window.google?.maps?.Size ? {
                  url: '/assets/start_marker.png',
                  scaledSize: new window.google.maps.Size(32, 32)
                } : undefined}
              />
            )}
            {end && (
              <Marker
                position={end}
                title="Destination"
                icon={window.google?.maps?.Size ? {
                  url: '/assets/end_marker.png',
                  scaledSize: new window.google.maps.Size(32, 32)
                } : undefined}
              />
            )}
            {directionsResponse && (
              <DirectionsRenderer directions={directionsResponse} />
            )}
          </GoogleMap>
        )}
      </Box>
    </Box>
  );
};

export default RideMap;
